import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import neuroService from '../../services/neuro.service';

export const fetchNeuroQuestions = createAsyncThunk('neuro/fetchQuestions', async () => {
  return neuroService.getQuestions();
});

export const fetchNeuroConsent = createAsyncThunk('neuro/fetchConsent', async () => {
  return neuroService.getConsent();
});

export const fetchKidNeuroStatus = createAsyncThunk('neuro/fetchKidStatus', async (kidId) => {
  return neuroService.getKidStatus(kidId);
});

export const startNeuroEvaluation = createAsyncThunk(
  'neuro/startEvaluation',
  async ({ kid_id, consent_version_id }) =>
    neuroService.startEvaluation({ kid_id, consent_version_id })
);

export const saveNeuroAnswers = createAsyncThunk(
  'neuro/saveAnswers',
  async ({ evaluationId, answers, current_position }) =>
    neuroService.saveAnswers(evaluationId, { answers, current_position })
);

export const submitNeuroEvaluation = createAsyncThunk(
  'neuro/submit',
  async (evaluationId) => neuroService.submitEvaluation(evaluationId)
);

export const fetchNeuroResult = createAsyncThunk('neuro/fetchResult', async (payload) => {
  const evaluationId = typeof payload === 'object' ? payload.evaluationId : payload;
  return neuroService.getResult(evaluationId);
});

const neuroSlice = createSlice({
  name: 'neuro',
  initialState: {
    questions: [],
    consent: null,
    evaluation: null,
    draft: null,
    result: null,
    resultKidId: null,
    kidStatus: null,
    kidStatusKidId: null,
    loading: false,
    error: null,
    localAnswers: {},
  },
  reducers: {
    setLocalAnswer: (state, action) => {
      const { questionId, value } = action.payload;
      state.localAnswers[questionId] = value;
    },
    clearNeuroState: (state) => {
      state.evaluation = null;
      state.draft = null;
      state.result = null;
      state.resultKidId = null;
      state.kidStatus = null;
      state.kidStatusKidId = null;
      state.localAnswers = {};
      state.error = null;
    },
    hydrateLocalAnswers: (state, action) => {
      state.localAnswers = action.payload || {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNeuroQuestions.pending, (state) => { state.loading = true; })
      .addCase(fetchNeuroQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.questions = action.payload;
      })
      .addCase(fetchNeuroQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchNeuroConsent.fulfilled, (state, action) => {
        state.consent = action.payload;
      })
      .addCase(fetchKidNeuroStatus.pending, (state) => {
        state.result = null;
        state.resultKidId = null;
      })
      .addCase(fetchKidNeuroStatus.fulfilled, (state, action) => {
        state.kidStatus = action.payload;
        state.kidStatusKidId = action.meta.arg;
        state.draft = action.payload?.draft || null;
      })
      .addCase(startNeuroEvaluation.fulfilled, (state, action) => {
        state.evaluation = action.payload;
        state.draft = action.payload;
      })
      .addCase(saveNeuroAnswers.fulfilled, (state, action) => {
        state.evaluation = action.payload;
      })
      .addCase(submitNeuroEvaluation.fulfilled, (state, action) => {
        state.result = action.payload;
        state.evaluation = null;
        state.draft = null;
      })
      .addCase(fetchNeuroResult.fulfilled, (state, action) => {
        state.result = action.payload;
        const kidId = typeof action.meta.arg === 'object' ? action.meta.arg.kidId : null;
        if (kidId != null) state.resultKidId = kidId;
      });
  },
});

export const { setLocalAnswer, clearNeuroState, hydrateLocalAnswers } = neuroSlice.actions;
export default neuroSlice.reducer;
